import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { C, gravityScaleAt } from '../game/constants';
import { World } from '../game/World';

/**
 * Your rig: a junk hauler welded together over the years.
 *
 * Flight model (atmosphere): a SHIP, not an elevator —
 *   throttle drives the MAIN REAR ENGINES along the hull axis,
 *   pitch angles the nose up/down to redirect that thrust,
 *   small belly lift-jets only matter near the ground for takeoff/landing.
 * Climb-outs look like climb-outs: forward speed first, then nose up.
 * She's flown from the glazed cockpit at the bow.
 */
export class Mothership {
  readonly group = new THREE.Group();          // position only
  private readonly hull = new THREE.Group();   // pitch rotation lives here
  vel = new THREE.Vector2();
  pitch = 0;                                   // radians, nose attitude
  facing = 1;                                  // 1 = bow +x, -1 = bow -x
  landed = false;
  private readonly fallback = new THREE.Group();   // primitives until the GLB lands
  private readonly mainFlame: THREE.Group;         // twin flames, one per bell column
  private gearNode: THREE.Object3D | null = null;  // LandingGear_Controller in the GLB
  private doorLeft: THREE.Object3D[] = [];         // bay door leaves (slide apart in x)
  private doorRight: THREE.Object3D[] = [];
  private readonly doorBaseX = new Map<THREE.Object3D, number>();
  private gearDeployed = false;
  private gearT = 0;                 // 0 = retracted, 1 = down
  private bayOpen = false;
  private doorHold = 0;              // seconds the doors stay open after a pulse
  private doorT = 0;                 // 0 = closed, 1 = open
  private readonly liftL: THREE.Mesh;
  private readonly liftR: THREE.Mesh;
  private topL!: THREE.Mesh;
  private topR!: THREE.Mesh;
  private sideJetsL!: THREE.Group;   // left-flank jets: exhaust LEFT, push RIGHT
  private sideJetsR!: THREE.Group;   // right-flank jets: exhaust RIGHT, push LEFT
  private turnJetsL!: THREE.Group;   // angled pair that torques the bow leftward
  private turnJetsR!: THREE.Group;   // angled pair that torques the bow rightward
  private rampX = 0;                 // throttle builds with hold time
  private rampY = 0;
  private yaw = 0;                   // actual hull heading, physics-driven
  private yawVel = 0;                // rad/s — turn jets apply torque to this
  private readonly beaconMat: THREE.MeshBasicMaterial;
  private beaconT = 0;

  constructor() {
    const rust = new THREE.MeshLambertMaterial({ color: 0x8a5a33 });
    const rustDark = new THREE.MeshLambertMaterial({ color: 0x6e462a });
    const steel = new THREE.MeshLambertMaterial({ color: 0x707a85 });
    const steelDark = new THREE.MeshLambertMaterial({ color: 0x4c545e });
    const glass = new THREE.MeshLambertMaterial({ color: 0xa8e7ff });
    const bayGlow = new THREE.MeshBasicMaterial({ color: 0xffc860 });
    this.beaconMat = new THREE.MeshBasicMaterial({ color: 0xff5a3a });
    const flameMat = new THREE.MeshBasicMaterial({
      color: 0xff8a2a, transparent: true, opacity: 0.9,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });

    // Main hull: two mismatched slabs bolted together, slightly askew
    const hullA = new THREE.Mesh(new THREE.BoxGeometry(16, 5, 7), rust);
    hullA.position.x = -5;
    const hullB = new THREE.Mesh(new THREE.BoxGeometry(12, 4.2, 6.4), steel);
    hullB.position.set(8, -0.3, 0);
    hullB.rotation.z = 0.02;

    // THE COCKPIT — glazed nose at the bow; this is where you fly her from
    const cockpit = new THREE.Mesh(new THREE.BoxGeometry(3.2, 2.4, 4.2), steelDark);
    cockpit.position.set(14.6, 0.6, 0);
    const canopy = new THREE.Mesh(new THREE.BoxGeometry(1.6, 1.3, 3), glass);
    canopy.position.set(15.6, 1.5, 0);
    const cabinLight = new THREE.PointLight(0xa8e7ff, 3, 10);
    cabinLight.position.set(15.4, 1.5, 1.5);

    // Patch plates welded on at odd angles
    const patches: [number, number, number, number, THREE.Material][] = [
      [-9, 1.8, 3.6, 0.15, steelDark],
      [-1, -1.5, 3.65, -0.1, rustDark],
      [5, 2, 3.3, 0.22, rustDark],
      [11, -1.2, 3.3, -0.18, steelDark],
    ];
    for (const [x, y, z, rot, mat] of patches) {
      const plate = new THREE.Mesh(new THREE.BoxGeometry(3.4, 2.2, 0.3), mat);
      plate.position.set(x, y, z);
      plate.rotation.z = rot;
      this.fallback.add(plate);
    }

    // Cargo containers strapped on top
    const containerColors = [0xa85c2a, 0x5c7a4a, 0x4a5d7a, 0x8a4a4a];
    for (let i = 0; i < 4; i++) {
      const c = new THREE.Mesh(
        new THREE.BoxGeometry(3.6, 2.2, 2.6),
        new THREE.MeshLambertMaterial({ color: containerColors[i] }),
      );
      c.position.set(-10 + i * 4.4, 3.7, (i % 2 === 0 ? 1 : -1) * 0.8);
      c.rotation.y = (i % 2 === 0 ? 1 : -1) * 0.08;
      this.fallback.add(c);
    }

    // Scaffold girders
    for (const [x1, y1] of [[-2, 3], [2.5, 2.6]] as const) {
      const girder = new THREE.Mesh(new THREE.BoxGeometry(0.35, 4.4, 0.35), steelDark);
      girder.position.set(x1, y1, 2.6);
      girder.rotation.z = 0.5;
      this.fallback.add(girder);
    }

    // Crooked antenna + blinking beacon
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.14, 5, 6), steelDark);
    mast.position.set(-11, 6.5, 0);
    mast.rotation.z = 0.18;
    const beacon = new THREE.Mesh(new THREE.SphereGeometry(0.42, 8, 6), this.beaconMat);
    beacon.position.set(-11.9, 9, 0);
    this.fallback.add(mast, beacon);

    // The dish (mostly works)
    const dish = new THREE.Mesh(
      new THREE.SphereGeometry(1.6, 10, 8, 0, Math.PI * 2, 0, Math.PI / 2.6), steel);
    dish.position.set(10.5, 3.6, 0);
    dish.rotation.z = -0.7;
    this.fallback.add(dish);

    // Hangar bay: warm work-lights
    const bay = new THREE.Mesh(new THREE.BoxGeometry(7, 1.1, 4.8), bayGlow);
    bay.position.set(-2, -3, 0);
    this.fallback.add(bay);

    // MAIN ENGINES: a cluster of mismatched bells at the STERN (-x)
    const bellBig = new THREE.Mesh(new THREE.CylinderGeometry(1.6, 2.0, 3.6, 8), rustDark);
    bellBig.rotation.z = Math.PI / 2;
    bellBig.position.set(-14, -0.6, 1.4);
    const bellSmall = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.3, 3, 8), steelDark);
    bellSmall.rotation.z = Math.PI / 2;
    bellSmall.position.set(-13.6, -1.6, -1.6);
    this.fallback.add(bellBig, bellSmall);

    // Main engine flame (rearward, drives you FORWARD)
    this.mainFlame = new THREE.Group();
    for (const dz of [3.3, -3.3]) {              // one flame per bell column
      const f = new THREE.Mesh(new THREE.ConeGeometry(1.35, 5.5, 8), flameMat);
      f.rotation.z = Math.PI / 2;
      f.position.set(-19.6, -0.3, dz);
      this.mainFlame.add(f);
    }
    this.mainFlame.visible = false;

    // Belly lift-jets — exhaust DOWN, force UP. Mounted MID + BOW so the
    // lift is forward of center: that's what tips the nose up on climbs.
    this.liftL = new THREE.Mesh(new THREE.ConeGeometry(0.7, 2.2, 8), flameMat);
    this.liftL.rotation.x = Math.PI;
    this.liftL.position.set(-6, -5.1, 0);        // mid-belly nozzle (rcsBellyMid)
    this.liftR = this.liftL.clone();
    this.liftR.position.set(11.5, -4.4, 0);      // bow-belly nozzle (rcsBellyBow)
    this.liftL.visible = this.liftR.visible = false;

    // Top-side RCS — exhaust UP, force DOWN. Also MID + BOW: pushing the
    // front down is what noses her into a descent.
    this.topL = new THREE.Mesh(new THREE.ConeGeometry(0.55, 1.8, 8), flameMat);
    this.topL.position.set(6.2, 4.2, 0);         // fore-deck top nozzle (rcsTopMid)
    this.topR = this.topL.clone();
    this.topR.position.set(12.7, 3.3, 0);        // nose-slope nozzle (RCS_TopNose)
    this.topL.visible = this.topR.visible = false;

    // Flank RCS lines — a row of small jets on each side. The side OPPOSITE
    // your motion fires: burn left → right-flank jets flare (pushing left).
    const makeFlank = (dir: number): THREE.Group => {
      const g = new THREE.Group();
      // dir +1 = bow tip ports (RCS_TipBow), dir -1 = stern ports flanking
      // the rear hatch (RCS_TipStern) — positions match the model's nozzles.
      const ports: [number, number][] = dir > 0
        ? [[17.6, 0.5], [17.6, -1.9]]
        : [[-18.9, 3.4], [-18.9, -3.5]];
      for (const [fx, fy] of ports) {
        const jet = new THREE.Mesh(new THREE.ConeGeometry(0.45, 1.6, 8), flameMat);
        jet.rotation.z = -dir * Math.PI / 2;   // exhaust points outboard
        jet.position.set(fx, fy, 0);
        g.add(jet);
      }
      g.visible = false;
      return g;
    };
    this.sideJetsL = makeFlank(-1);
    this.sideJetsR = makeFlank(1);

    // TURN JETS: angled pairs mounted mid-ship. Firing a bow-corner jet and
    // the opposite stern-corner jet makes a torque couple — that visible
    // force is what actually swings her around.
    const makeTurnPair = (dir: number): THREE.Group => {
      const g = new THREE.Group();
      const bow = new THREE.Mesh(new THREE.ConeGeometry(0.5, 1.7, 8), flameMat);
      bow.position.set(6.5, dir > 0 ? 3.9 : -4.3, 0);      // RCS_TurnBow station
      bow.rotation.z = -dir * (Math.PI / 2 - 0.6);   // angled outward ~55°
      const stern = new THREE.Mesh(new THREE.ConeGeometry(0.5, 1.7, 8), flameMat);
      stern.position.set(-11.5, dir > 0 ? -4.3 : 3.9, 0);  // RCS_TurnStern station
      stern.rotation.z = dir * (Math.PI / 2 - 0.6) + Math.PI;
      g.add(bow, stern);
      g.visible = false;
      return g;
    };
    this.turnJetsL = makeTurnPair(1);    // bow-top + stern-bottom → nose left
    this.turnJetsR = makeTurnPair(-1);   // bow-bottom + stern-top → nose right

    // EVERY jet is childed to the hull — they are mounted on the ship and
    // ride every yaw and pitch with it. Which side fires is resolved in
    // world terms each frame (facing-aware) in updateSideJets.
    this.fallback.add(hullA, hullB, cockpit, canopy, cabinLight);
    this.hull.add(this.fallback,
      this.mainFlame, this.liftL, this.liftR, this.topL, this.topR,
      this.turnJetsL, this.turnJetsR, this.sideJetsL, this.sideJetsR);
    this.group.add(this.hull);
    this.loadModel();
    this.group.position.set(0, C.orbit.altitude, 0);
  }

  get pos(): THREE.Vector3 { return this.group.position; }

  /** Swap in the Blender-built rig; primitives remain as loading fallback. */
  private loadModel(): void {
    new GLTFLoader().load('models/rig.glb', (gltf) => {
      this.fallback.visible = false;
      this.hull.add(gltf.scene);
      // Animatable hardware: retractable gear + sliding bay door leaves
      this.gearNode = gltf.scene.getObjectByName('LandingGear_Controller') ?? null;
      gltf.scene.traverse((o) => {
        if (o.name.startsWith('MiningBayDoorLeft_')) this.doorLeft.push(o);
        else if (o.name.startsWith('MiningBayDoorRight_')) this.doorRight.push(o);
      });
      for (const o of [...this.doorLeft, ...this.doorRight])
        this.doorBaseX.set(o, o.position.x);
      this.applyGearAndDoors();
    }, undefined, () => { /* keep primitives on failure */ });
  }

  /** Bay doors: open/close (docking scene) or pulse open for a few seconds
   *  (dropping the lander). Gear is automatic — see fly(). */
  setBay(open: boolean): void { this.bayOpen = open; }
  pulseBay(seconds: number): void { this.doorHold = seconds; }

  private applyGearAndDoors(): void {
    if (this.gearNode) this.gearNode.position.y = (1 - this.gearT) * 3.2;
    for (const o of this.doorLeft)
      o.position.x = (this.doorBaseX.get(o) ?? 0) - this.doorT * 2.5;
    for (const o of this.doorRight)
      o.position.x = (this.doorBaseX.get(o) ?? 0) + this.doorT * 2.5;
  }

  tick(dt: number): void {
    this.beaconT += dt;
    this.beaconMat.color.setHex(
      Math.sin(this.beaconT * 5) > 0 ? 0xff5a3a : 0x521c12);
    // Gear and bay doors ease toward their targets every frame
    this.gearT = THREE.MathUtils.damp(this.gearT, this.gearDeployed ? 1 : 0, 3, dt);
    this.doorHold = Math.max(0, this.doorHold - dt);
    this.doorT = THREE.MathUtils.damp(
      this.doorT, this.bayOpen || this.doorHold > 0 ? 1 : 0, 2.5, dt);
    this.applyGearAndDoors();
  }

  private nearGround(world: World): boolean {
    return this.pos.y < world.surfaceYAt(this.pos.x) + 18;
  }

  /** Throttle builds while held (~1.1s to full) and dies fast on release —
   *  taps nudge her, committed holds wind the engines up. */
  private updateRamps(dt: number, moveX: number, moveY: number): void {
    const build = 0.9 * dt, decay = 3 * dt;
    this.rampX = moveX !== 0
      ? THREE.MathUtils.clamp(this.rampX + Math.sign(moveX) * build, -1, 1)
      : THREE.MathUtils.damp(this.rampX, 0, 8, dt);
    this.rampY = moveY !== 0
      ? THREE.MathUtils.clamp(this.rampY + Math.sign(moveY) * build, -1, 1)
      : THREE.MathUtils.damp(this.rampY, 0, 8, dt);
    // Direction reversal cuts the old ramp quickly
    if (moveX !== 0 && Math.sign(this.rampX) !== Math.sign(moveX))
      this.rampX += Math.sign(moveX) * decay;
    if (moveY !== 0 && Math.sign(this.rampY) !== Math.sign(moveY))
      this.rampY += Math.sign(moveY) * decay;
  }

  /** Flank RCS: the WORLD side opposite the burn flares. The jets are hull
   *  children, so when the hull is yawed the local side that currently
   *  faces that world direction is the one that lights. */
  private updateSideJets(rampX: number): void {
    const mag = Math.abs(rampX);
    // cos(yaw) ≈ which world direction the local right flank faces
    const localRightFacesWorldRight = Math.cos(this.yaw) >= 0;
    const wantWorldRightExhaust = rampX < -0.08;   // pushing LEFT
    const wantWorldLeftExhaust = rampX > 0.08;     // pushing RIGHT
    this.sideJetsR.visible =
      (wantWorldRightExhaust && localRightFacesWorldRight) ||
      (wantWorldLeftExhaust && !localRightFacesWorldRight);
    this.sideJetsL.visible =
      (wantWorldLeftExhaust && localRightFacesWorldRight) ||
      (wantWorldRightExhaust && !localRightFacesWorldRight);
    const s = 0.5 + mag * 0.8 + Math.random() * 0.25;
    for (const g of [this.sideJetsL, this.sideJetsR])
      if (g.visible) g.children.forEach((j) => j.scale.set(s, s, s));
  }

  /**
   * Atmospheric flight: throttle = main engines along the hull axis
   * (bow = +x), pitchIn angles the nose. Near the ground, pushing up
   * fires the belly jets straight down for a proper liftoff hover.
   * Returns true while entry-heat conditions are met.
   */
  /**
   * SIMPLE directional flight: press a direction, she accelerates that way.
   * All the ship-feel — bow swinging around, nose rising on climbs, banking
   * — is automatic presentation driven by her velocity, not a control.
   * Returns true while entry-heat conditions are met.
   */
  fly(dt: number, moveX: number, moveY: number, world: World): boolean {
    const m = C.mothership;
    const nearGround = this.nearGround(world);
    if (Math.abs(moveX) < 0.12) moveX = 0;
    if (Math.abs(moveY) < 0.12) moveY = 0;
    this.updateRamps(dt, moveX, moveY);

    this.vel.x += this.rampX * m.accel * dt;
    this.vel.y += this.rampY * m.accel * dt;
    this.vel.y += C.ship.gravity * 0.35 * gravityScaleAt(this.pos.y) * dt;
    this.vel.x *= Math.pow(m.drag, dt * 60);
    this.vel.y *= Math.pow(m.drag, dt * 60);
    this.vel.clampLength(0, m.maxSpeed);

    if (this.landed && moveY > 0.1) this.landed = false;
    if (this.landed) {
      this.vel.set(0, 0);
    } else {
      this.pos.x += this.vel.x * dt;
      this.pos.y += this.vel.y * dt;
    }

    const halfW = C.world.cols / 2 - 14;
    this.pos.x = THREE.MathUtils.clamp(this.pos.x, -halfW, halfW);

    // Touchdown
    const ground = world.surfaceYAt(this.pos.x) + 6.2;
    if (this.pos.y <= ground && this.vel.y <= 0) {
      this.pos.y = ground;
      this.vel.set(0, 0);
      this.landed = true;
    }

    this.autoAttitude(dt, moveX);
    // Thrusters show WHENEVER they're pushing, at any altitude: belly jets
    // for climbs, top RCS for down-burns, flank RCS + mains for horizontal.
    this.mainFlame.visible = !this.landed && Math.abs(this.rampX) > 0.15;
    if (this.mainFlame.visible) this.setMainFlameScale();
    this.updateSideJets(this.landed ? 0 : this.rampX);
    const flick = (0.5 + Math.abs(this.rampY) * 0.7) * (0.8 + Math.random() * 0.4);
    this.liftL.visible = this.liftR.visible = !this.landed && this.rampY > 0.08;
    if (this.liftL.visible) this.liftL.scale.y = this.liftR.scale.y = flick;
    this.topL.visible = this.topR.visible = !this.landed && this.rampY < -0.08;
    if (this.topL.visible) this.topL.scale.y = this.topR.scale.y = flick;
    // Gear drops for the landing, tucks away once she's climbing out
    this.gearDeployed = this.landed || (nearGround && this.vel.y < 2);

    return this.pos.y < C.mothership.entryHeatBelow && this.vel.y < -3;
  }

  /** Each bell-column flame stretches with throttle (children scaled so the
   *  group's mount positions stay put). */
  private setMainFlameScale(): void {
    const s = (0.4 + Math.abs(this.rampX)) * (0.8 + Math.random() * 0.4);
    this.mainFlame.children.forEach((f) => { f.scale.x = s; });
  }

  /** Velocity-driven presentation: bow faces travel, nose follows climb. */
  private autoAttitude(dt: number, moveX: number): void {
    if (moveX > 0.2) this.facing = 1;
    else if (moveX < -0.2) this.facing = -1;
    const climb = THREE.MathUtils.clamp(this.vel.y * 0.03, -0.4, 0.45);
    this.pitch = THREE.MathUtils.lerp(this.pitch, this.landed ? 0 : climb, dt * 3);
    this.applyHullPose(dt, this.pitch);
  }

  /** Yaw the hull around to the current facing and pitch the nose — the
   *  whole ship (engines, flame, cockpit) swings with it. */
  /**
   * Torque-driven turning: the angled mid-ship jet pairs apply a GRADUAL
   * rotational force. The bow swings because the visible jets are pushing
   * it around — angular velocity builds, carries, and damps like mass.
   */
  private applyHullPose(dt: number, pitch: number): void {
    const targetYaw = this.facing === 1 ? 0 : Math.PI;
    const err = targetYaw - this.yaw;

    let turning = 0;
    if (Math.abs(err) > 0.03) {
      turning = Math.sign(err);
      this.yawVel += turning * 2.4 * dt;          // jet torque
    }
    this.yawVel *= Math.pow(0.94, dt * 60);        // rotational damping
    this.yawVel = THREE.MathUtils.clamp(this.yawVel, -1.5, 1.5);
    this.yaw += this.yawVel * dt;
    // Settle instead of oscillating forever around the target
    if (Math.abs(err) < 0.05 && Math.abs(this.yawVel) < 0.25) {
      this.yaw = targetYaw;
      this.yawVel = 0;
      turning = 0;
    }
    this.yaw = THREE.MathUtils.clamp(this.yaw, -0.2, Math.PI + 0.2);

    this.hull.rotation.y = this.yaw;
    // (XYZ euler: the yaw flip keeps +pitch reading nose-UP in both facings.)
    this.hull.rotation.z = pitch;

    // The pair doing the pushing is lit, flickering with effort.
    this.turnJetsL.visible = turning > 0;
    this.turnJetsR.visible = turning < 0;
    const s = 0.8 + Math.random() * 0.5;
    for (const g of [this.turnJetsL, this.turnJetsR])
      if (g.visible) g.children.forEach((j) => j.scale.set(s, s, s));
  }

  /** Open-space flight: same simple directional scheme, faster out here. */
  flySpace(dt: number, moveX: number, moveY: number): void {
    const m = C.mothership;
    if (Math.abs(moveX) < 0.12) moveX = 0;
    if (Math.abs(moveY) < 0.12) moveY = 0;
    this.updateRamps(dt, moveX, moveY);

    this.vel.x += this.rampX * m.accel * 2.4 * dt;
    this.vel.y += this.rampY * m.accel * 2.4 * dt;
    this.vel.x *= Math.pow(m.drag, dt * 60);
    this.vel.y *= Math.pow(m.drag, dt * 60);
    this.vel.clampLength(0, m.maxSpeed * 1.9);

    this.pos.x = THREE.MathUtils.clamp(
      this.pos.x + this.vel.x * dt, -C.space.bounds.x, C.space.bounds.x);
    this.pos.y = THREE.MathUtils.clamp(
      this.pos.y + this.vel.y * dt, -C.space.bounds.y, C.space.bounds.y);

    this.autoAttitude(dt, moveX);
    // Every force is visible: main engines + flank RCS for horizontal,
    // belly jets (exhaust down) for climbs, top RCS (exhaust up) for
    // descents — all scaled by how long you've held the burn.
    this.mainFlame.visible = Math.abs(this.rampX) > 0.15;
    if (this.mainFlame.visible) this.setMainFlameScale();
    this.gearDeployed = false;                    // no gear in open space
    this.updateSideJets(this.rampX);
    const flick = (0.5 + Math.abs(this.rampY) * 0.7) * (0.8 + Math.random() * 0.4);
    this.liftL.visible = this.liftR.visible = this.rampY > 0.08;
    if (this.liftL.visible) this.liftL.scale.y = this.liftR.scale.y = flick;
    this.topL.visible = this.topR.visible = this.rampY < -0.08;
    if (this.topL.visible) this.topL.scale.y = this.topR.scale.y = flick;
  }

  haltVisuals(): void {
    this.mainFlame.visible = this.liftL.visible = this.liftR.visible = false;
    this.topL.visible = this.topR.visible = false;
    this.turnJetsL.visible = this.turnJetsR.visible = false;
    this.hull.rotation.z = 0;
    this.pitch = 0;
    this.yawVel = 0;
  }
}
